<template>
  <div class="flex flex-col">
    <div class="flex w-full justify-end items-center px-8 pt-4 pb-2 sticky top-0 bg-base-100">
      <label class="form-control w-full">
        <div class="label">
          <span class="label-text text-accent text-semibold">{{ $t('comments') }}</span>
        </div>
        <div class="flex">
          <form class="w-full" @submit.prevent="postComment">
            <input
              v-model="newComment"
              :disabled="loadingComments"
              class="input input-bordered h-12 w-full"
              :placeholder="$t('addComment')"
            />
          </form>
          <button
            class="btn btn-primary btn-square ml-2"
            :disabled="loadingComments"
            @click="postComment"
          >
            <Cog6ToothIcon v-if="loadingComments" class="h-6 w-6 text-accent animate-spin" />
            <PaperAirplaneIcon v-else class="h-6 w-6 text-accent" />
          </button>
        </div>
      </label>
    </div>

    <div v-if="comments.length > 0" class="flex flex-col space-y-2 p-8">
      <Comment v-for="comment of comments" :key="comment._id" :comment="comment" />
    </div>
    <div v-else class="text-primary/25 italic text-2xl text-center mt-10">
      {{ $t('noCommentsFound') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

import { type CommentModel } from '@/interfaces'
import { usePinsStore } from '@/stores/pins'
import { Cog6ToothIcon, PaperAirplaneIcon } from '@heroicons/vue/24/solid'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toast-notification'

import { axiosInstance } from '@/utils/axios'

import Comment from '@/components/Comment.vue'

const toast = useToast({ position: 'top-right' })
const { t } = useI18n()

const pinsStore = usePinsStore()
const { updatePin } = pinsStore
const { selectedPin } = storeToRefs(pinsStore)

const authToken = ref('')
const comments = ref<CommentModel[]>([])
const newComment = ref('')
const loadingComments = ref(false)

const emit = defineEmits<{
  (e: 'update'): void
  (e: 'show-overview'): void
}>()

const fetchActivityComments = async () => {
  loadingComments.value = true

  if (selectedPin.value) {
    const { data } = await axiosInstance.get(`comments/aid/${selectedPin.value._id}`)

    comments.value = data as CommentModel[]
  }

  loadingComments.value = false
}

const postComment = async () => {
  loadingComments.value = true

  if (newComment.value.length < 10) {
    toast.error(t('commentLengthRequirement'))
    loadingComments.value = false
    return
  }

  if (selectedPin.value) {
    const res = await axiosInstance.post(`comments`, {
      activity_id: selectedPin.value._id,
      comment: newComment.value
    })

    newComment.value = ''

    if (res.data.updatedActivity) {
      updatePin(res.data.updatedActivity)
    }

    fetchActivityComments()
  }

  loadingComments.value = false
}

onMounted(async () => {
  const storedToken = sessionStorage.getItem('authToken')

  authToken.value = storedToken ? storedToken : ''

  fetchActivityComments()
})
</script>
